import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type {
  Project,
  SalesTrackerDealBoard,
  SalesTrackerDealBoardParams,
} from '@/lib/types';

/**
 * The sales tracker board — load it, reload it.
 *
 * 🔴 **The rows are DEALS now — owner's decision 2026-10-12.** *SUPERSEDED: the
 * pursuit board's optimistic `movePursuit`, its `moveError` and its
 * `movingPursuitId`.* A deal's stage changes through an ordinary
 * `PATCH /deals/{id}` from the stage cell, and the page re-reads the board
 * afterwards — the counts (`accepted` is quote-derived) and every band the deal
 * appears in are the server's to restate, never this hook's to guess.
 *
 * Every query change is debounced by {@link DEBOUNCE_MS} and aborts the request
 * it supersedes — a request per keystroke is wrong at any rate limit.
 */

/** Long enough to swallow a burst of keystrokes, short enough not to feel laggy. */
const DEBOUNCE_MS = 250;

/**
 * Serialise the query into a stable string.
 *
 * The params reach this hook as an **object literal built in the caller's
 * render** — a new reference every render, and an infinite fetch loop the
 * instant it lands in a dependency array. A string compares by value, so the
 * effect depends on THIS. It is also the exact param object to send, recovered
 * with `JSON.parse`, so the key and the request cannot drift apart.
 *
 * ⚠️ **This must keep every value type the param type can carry** — anything
 * dropped here is dropped between the URL and the request, which is the one gap
 * no server error can report.
 */
function paramsKeyOf(params: SalesTrackerDealBoardParams): string {
  const stable: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params).sort(([a], [b]) => (a < b ? -1 : 1))) {
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      continue;
    }
    if (value === '') continue;
    stable[key] = value;
  }
  return JSON.stringify(stable);
}

export interface UseSalesTrackerResult {
  board: SalesTrackerDealBoard | null;
  /** First load only. */
  loading: boolean;
  /** A reload with data already on screen — for `aria-busy` + `opacity-60`. */
  refetching: boolean;
  error: string | null;
  reload: () => void;
  /**
   * A reload that neither blanks nor dims the board — for a refresh the reader
   * did not ask for (a note written in the drawer). A failure is still reported
   * through `error`, with the board kept.
   */
  refreshQuietly: () => void;
}

/**
 * @param params The whole board query, or `null` while no cycle is resolved.
 *   A fresh object literal every render is expected and safe.
 */
export function useSalesTracker(
  params: SalesTrackerDealBoardParams | null,
  enabled: boolean,
): UseSalesTrackerResult {
  const [board, setBoard] = useState<SalesTrackerDealBoard | null>(null);
  const [loadingState, setLoadingState] = useState(enabled && params !== null);
  const [refetchingState, setRefetchingState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  /** The truth about what is on screen, read inside the effect without depending on it. */
  const boardRef = useRef<SalesTrackerDealBoard | null>(null);
  /** Set by `reload()` so a deliberate refresh does not sit through the debounce. */
  const skipDebounceRef = useRef(false);
  /** Set by `refreshQuietly()` so the next fetch skips the dim. One-shot. */
  const quietRef = useRef(false);

  /*
   * Derived during render rather than cleared from the effect, so toggling away
   * mid-load cannot leave the board stuck behind a skeleton or dimmed.
   */
  const active = enabled && params !== null;
  const loading = active && loadingState;
  const refetching = active && refetchingState;

  const reload = useCallback(() => {
    skipDebounceRef.current = true;
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const refreshQuietly = useCallback(() => {
    quietRef.current = true;
    skipDebounceRef.current = true;
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const paramsKey = params === null ? null : paramsKeyOf(params);

  useEffect(() => {
    if (!active || paramsKey === null) return;

    const controller = new AbortController();
    const { signal } = controller;
    const delay = skipDebounceRef.current ? 0 : DEBOUNCE_MS;
    skipDebounceRef.current = false;
    // Read and cleared here, so a quiet refresh superseded by a filter change
    // does not silence that later, deliberate load.
    const quiet = quietRef.current;
    quietRef.current = false;

    const timer = setTimeout(() => {
      if (boardRef.current) {
        if (!quiet) setRefetchingState(true);
      } else setLoadingState(true);

      api
        .salesTrackerBoard(JSON.parse(paramsKey) as SalesTrackerDealBoardParams, signal)
        .then((response) => {
          if (signal.aborted) return;
          boardRef.current = response.data;
          setBoard(response.data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          // The stale board stays on screen underneath.
          setError(
            caught instanceof Error ? caught.message : 'Failed to load the sales tracker.',
          );
        })
        .finally(() => {
          if (signal.aborted) return;
          setLoadingState(false);
          setRefetchingState(false);
        });
    }, delay);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, paramsKey, reloadNonce]);

  return { board, loading, refetching, error, reload, refreshQuietly };
}

/* ------------------------------------------------------------------ *
 * The project filter's vocabulary
 * ------------------------------------------------------------------ */

export interface CycleProjectOptionsState {
  projects: { id: number; name: string }[];
  loading: boolean;
  failed: boolean;
  /** More projects in this cycle than one page holds — the caller must say so. */
  hasMore: boolean;
}

/**
 * Every project in one cycle, for the board's project FILTER to offer.
 *
 * 🔴 **A SEPARATE READ, NEVER DERIVED FROM `board.lanes`**, which the filter
 * itself narrows: a vocabulary built from them collapses to whatever is already
 * on screen. Read the master; never build a picklist out of the response you are
 * filtering.
 *
 * ⚠️ **NO `isActive` FILTER** — a retired project keeps its band and must be
 * filterable. **NO `sort`** — the endpoint's default manual order is the order
 * the bands arrive in.
 */
export function useCycleProjectOptions(
  cycleId: number | null,
  enabled: boolean,
): CycleProjectOptionsState {
  /**
   * ONE state object CARRYING THE CYCLE IT BELONGS TO, so last cycle's projects
   * can never be returned as this cycle's options.
   */
  const [loaded, setLoaded] = useState<{
    cycleId: number;
    projects: { id: number; name: string }[];
    hasMore: boolean;
    failed: boolean;
  } | null>(null);

  const active = enabled && cycleId !== null;
  const fresh = active && loaded !== null && loaded.cycleId === cycleId;

  useEffect(() => {
    if (!active) return;

    const controller = new AbortController();
    // Every page (2026-10-05): one page of 100 silently drops a cycle's tail.
    api
      .listAllProjects(controller.signal, { cycleId })
      .then((all: Project[]) => {
        if (controller.signal.aborted) return;
        setLoaded({
          cycleId,
          projects: all.map((project) => ({ id: project.id, name: project.name })),
          hasMore: false,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // A failed read is NOT "this cycle has no projects".
        setLoaded({ cycleId, projects: [], hasMore: false, failed: true });
      });

    return () => controller.abort();
  }, [active, cycleId]);

  return fresh && loaded !== null
    ? {
        projects: loaded.projects,
        loading: false,
        failed: loaded.failed,
        hasMore: loaded.hasMore,
      }
    : { projects: [], loading: active, failed: false, hasMore: false };
}
